<template>
<div class="mp-root" :style="themePageStyle">
  <nav class="mp-topbar">
    <div class="container d-flex align-items-center py-2">
      <a href="javascript:void(0)" @click="$router.go(-1)" class="mp-back"><i class="mdi mdi-arrow-left"></i></a>
      <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">My Profile</span>
    </div>
  </nav>

  <div class="container mp-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <div v-else class="row g-4">
      <!-- Sidebar tabs -->
      <div class="col-lg-3">
        <div class="mp-card mp-tabs">
          <button class="mp-tab" :class="{'mp-tab-active':tab==='profile'}" @click="tab='profile'">
            <i class="mdi mdi-account-circle me-2"></i>Profile
          </button>
          <button class="mp-tab" :class="{'mp-tab-active':tab==='phone'}" @click="tab='phone'">
            <i class="mdi mdi-cellphone me-2"></i>Phone Number
          </button>
          <button class="mp-tab" :class="{'mp-tab-active':tab==='email'}" @click="tab='email'">
            <i class="mdi mdi-email me-2"></i>Email Address
          </button>
          <button class="mp-tab" :class="{'mp-tab-active':tab==='verification'}" @click="tab='verification';loadVerifications()">
            <i class="mdi mdi-shield-check me-2"></i>Verification
            <span v-if="verifVerifiedCount" class="mp-verif-pill mp-verif-pill-ok">{{ verifVerifiedCount }}</span>
            <span v-else-if="verifPendingCount" class="mp-verif-pill mp-verif-pill-pending">{{ verifPendingCount }}</span>
          </button>
        </div>
      </div>

      <div class="col-lg-9">
        <!-- ============ PROFILE TAB ============ -->
        <div v-if="tab==='profile'" class="mp-card">
          <h6 class="mp-section-title">Personal Information</h6>

          <div class="d-flex align-items-center gap-3 mb-4">
            <user-avatar v-if="avatarPreview" :src="avatarPreview" :name="user.display_name" size="xl" />
            <user-avatar v-else :src="form.avatar_url" :name="user.display_name" size="xl" />
            <div>
              <button class="btn btn-sm btn-outline-warning" @click="showCropper=true">
                <i class="mdi mdi-camera me-1"></i>{{ (form.avatar_url || avatarPreview) ? 'Change photo' : 'Upload or take photo' }}
              </button>
              <button v-if="avatarPreview" class="btn btn-sm btn-link text-muted" @click="avatarPreview='';avatarFile=null">Discard</button>
              <div class="mp-hint">Crop to a circle · auto-compressed to ~300KB · webcam or phone camera supported.</div>
            </div>
          </div>

          <avatar-cropper v-if="showCropper" @close="showCropper=false" @cropped="onCropped" />

          <div class="row g-3">
            <div class="col-md-6">
              <label class="mp-label">First name</label>
              <input v-model="form.first_name" class="form-control mp-input" placeholder="e.g. Alex" />
            </div>
            <div class="col-md-6">
              <label class="mp-label">Last name</label>
              <input v-model="form.last_name" class="form-control mp-input" placeholder="e.g. Wanjala" />
            </div>
            <div class="col-md-6">
              <label class="mp-label">County</label>
              <select v-model="form.county_id" class="form-select mp-input" @change="onCountyChange">
                <option value="">Select county…</option>
                <option v-for="c in counties" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="mp-label">Sub-county</label>
              <select v-model="form.sub_county_id" class="form-select mp-input" :disabled="!form.county_id">
                <option value="">Select sub-county…</option>
                <option v-for="s in subCounties" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="mp-label">Address details</label>
              <input v-model="form.address_details" class="form-control mp-input" placeholder="Building, road, landmark" />
            </div>
            <div class="col-md-6">
              <label class="mp-label">Preferred language</label>
              <select v-model="form.preferred_language" class="form-select mp-input">
                <option value="en">English</option>
                <option value="sw">Kiswahili</option>
              </select>
            </div>
            <div class="col-md-6 d-flex align-items-end">
              <div class="mp-locked w-100">
                <i class="mdi mdi-lock-outline me-1"></i>Phone &amp; email are changed in their own tabs
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-4">
            <button class="btn btn-warning fw-bold" @click="saveProfile" :disabled="saving">
              <span v-if="saving"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span>
              <span v-else><i class="mdi mdi-content-save me-1"></i>Save changes</span>
            </button>
            <span v-if="profileSaved" class="mp-saved"><i class="mdi mdi-check-circle me-1"></i>Saved</span>
            <span v-if="profileError" class="mp-error">{{ profileError }}</span>
          </div>
        </div>

        <!-- ============ PHONE TAB ============ -->
        <div v-if="tab==='phone'" class="mp-card">
          <h6 class="mp-section-title">Change phone number</h6>
          <div class="mp-current-pill">
            <i class="mdi mdi-cellphone me-1"></i>Current: <strong>{{ form.phone || '—' }}</strong>
            <span v-if="user.phone_verified_at" class="mp-verified ms-2"><i class="mdi mdi-check-decagram"></i> verified</span>
          </div>

          <div v-if="phoneStep==='input'">
            <label class="mp-label mt-3">New phone number</label>
            <input v-model="phoneForm.newPhone" class="form-control mp-input" placeholder="0712345678 or 254712345678" />
            <div class="mp-hint">We'll send a 6-digit code via SMS to confirm.</div>
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-warning fw-bold" @click="requestPhoneChange" :disabled="phoneBusy || !phoneForm.newPhone">
                <span v-if="phoneBusy"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
                <span v-else><i class="mdi mdi-send me-1"></i>Send verification code</span>
              </button>
            </div>
            <div v-if="phoneError" class="mp-error mt-2">{{ phoneError }}</div>
          </div>

          <div v-if="phoneStep==='verify'">
            <div class="mp-info mt-3">
              <i class="mdi mdi-information-outline me-1"></i>Code sent to <strong>{{ phoneForm.newPhone }}</strong>. Expires in 10 minutes.
            </div>
            <label class="mp-label mt-3">Enter 6-digit code</label>
            <input v-model="phoneForm.code" class="form-control mp-input mp-otp" maxlength="6" placeholder="••••••" />
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-warning fw-bold" @click="confirmPhoneChange" :disabled="phoneBusy || !phoneForm.code">
                <span v-if="phoneBusy"><span class="spinner-border spinner-border-sm me-1"></span>Verifying…</span>
                <span v-else><i class="mdi mdi-check-circle me-1"></i>Confirm change</span>
              </button>
              <button class="btn btn-outline-light" @click="phoneStep='input';phoneError=''">Use a different number</button>
              <button class="btn btn-link" @click="requestPhoneChange" :disabled="phoneBusy"><i class="mdi mdi-refresh me-1"></i>Resend</button>
            </div>
            <div v-if="phoneError" class="mp-error mt-2">{{ phoneError }}</div>
          </div>

          <div v-if="phoneStep==='done'" class="mp-success mt-3">
            <i class="mdi mdi-check-decagram me-1"></i>Phone number updated to <strong>{{ form.phone }}</strong>.
          </div>
        </div>

        <!-- ============ EMAIL TAB ============ -->
        <div v-if="tab==='email'" class="mp-card">
          <h6 class="mp-section-title">Change email address</h6>
          <div class="mp-current-pill">
            <i class="mdi mdi-email me-1"></i>Current: <strong>{{ form.email || '—' }}</strong>
            <span v-if="user.email_verified_at" class="mp-verified ms-2"><i class="mdi mdi-check-decagram"></i> verified</span>
          </div>

          <div v-if="emailStep==='input'">
            <label class="mp-label mt-3">New email address</label>
            <input v-model="emailForm.newEmail" type="email" class="form-control mp-input" placeholder="you@example.com" />
            <div class="mp-hint">We'll send a 6-digit code to the new address. Code expires in 30 minutes.</div>
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-warning fw-bold" @click="requestEmailChange" :disabled="emailBusy || !emailForm.newEmail">
                <span v-if="emailBusy"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
                <span v-else><i class="mdi mdi-send me-1"></i>Send verification code</span>
              </button>
            </div>
            <div v-if="emailError" class="mp-error mt-2">{{ emailError }}</div>
          </div>

          <div v-if="emailStep==='verify'">
            <div class="mp-info mt-3">
              <i class="mdi mdi-information-outline me-1"></i>Code sent to <strong>{{ emailForm.newEmail }}</strong>. Check your inbox + spam folder.
            </div>
            <label class="mp-label mt-3">Enter 6-digit code</label>
            <input v-model="emailForm.code" class="form-control mp-input mp-otp" maxlength="6" placeholder="••••••" />
            <div class="d-flex gap-2 mt-3">
              <button class="btn btn-warning fw-bold" @click="confirmEmailChange" :disabled="emailBusy || !emailForm.code">
                <span v-if="emailBusy"><span class="spinner-border spinner-border-sm me-1"></span>Verifying…</span>
                <span v-else><i class="mdi mdi-check-circle me-1"></i>Confirm change</span>
              </button>
              <button class="btn btn-outline-light" @click="emailStep='input';emailError=''">Use a different email</button>
              <button class="btn btn-link" @click="requestEmailChange" :disabled="emailBusy"><i class="mdi mdi-refresh me-1"></i>Resend</button>
            </div>
            <div v-if="emailError" class="mp-error mt-2">{{ emailError }}</div>
          </div>

          <div v-if="emailStep==='done'" class="mp-success mt-3">
            <i class="mdi mdi-check-decagram me-1"></i>Email updated to <strong>{{ form.email }}</strong>.
          </div>
        </div>

        <!-- ============ VERIFICATION TAB ============ -->
        <div v-if="tab==='verification'" class="mp-card">
          <h6 class="mp-section-title">Identity Verification</h6>
          <p class="mp-hint" style="margin-bottom:14px;">
            A Verified badge increases trust on your listings, vet bookings, and forum posts.
            Submit a national ID, passport, or business permit and our team will review within 24 hours.
          </p>

          <div v-if="verifLoading" class="text-center py-3"><span class="spinner-border spinner-border-sm me-1"></span> Loading…</div>

          <div v-else>
            <!-- Status cards for each existing submission -->
            <div v-for="v in verifications" :key="v.id" class="mp-verif-card" :class="'mp-verif-card-' + v.verification_status">
              <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <div>
                  <div class="mp-verif-type">{{ formatVerifType(v.verification_type) }}</div>
                  <div class="mp-verif-date">Submitted {{ formatDate(v.created_at) }}</div>
                </div>
                <div class="mp-verif-status" :class="'mp-verif-status-' + v.verification_status">
                  <i class="mdi" :class="verifIcon(v.verification_status)"></i>
                  {{ verifLabel(v.verification_status) }}
                </div>
              </div>
              <div v-if="v.verification_status === 'rejected' && v.rejection_reason" class="mp-verif-reason">
                <strong>Reason:</strong> {{ v.rejection_reason }}
              </div>
              <div v-if="v.verification_status === 'verified' && v.verified_at" style="font-size:11px;color:#50C878;">
                <i class="mdi mdi-shield-check me-1"></i>Verified on {{ formatDate(v.verified_at) }}
              </div>
            </div>

            <!-- Submit/Re-submit -->
            <div v-if="canSubmitVerification" class="mt-3">
              <button class="btn btn-warning fw-bold" @click="$router.push('/verification')">
                <i class="mdi mdi-upload me-1"></i>{{ verifications.length ? 'Submit a new verification' : 'Submit verification' }}
              </button>
            </div>

            <div v-if="!verifications.length && !verifLoading" class="mp-verif-empty">
              <i class="mdi mdi-shield-account-outline" style="font-size:36px;color:#6B6356;display:block;margin-bottom:6px;"></i>
              <div style="font-size:13px;color:#A09888;margin-bottom:12px;">No verification submitted yet.</div>
              <button class="btn btn-warning fw-bold" @click="$router.push('/verification')">
                <i class="mdi mdi-shield-plus me-1"></i>Get Verified
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
import AvatarCropper from "@/components/flockr-users/AvatarCropper.vue";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

export default {
  name: "MyProfile",
  components: { AvatarCropper, UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:true, saving:false,
    tab:'profile',
    user:{},
    form:{first_name:'',last_name:'',email:'',phone:'',avatar_url:'',county_id:'',sub_county_id:'',address_details:'',preferred_language:'en'},
    avatarFile:null, avatarPreview:'', showCropper:false,
    counties:[], subCounties:[],
    profileSaved:false, profileError:'',
    phoneStep:'input', phoneBusy:false, phoneError:'',
    phoneForm:{newPhone:'', code:''},
    emailStep:'input', emailBusy:false, emailError:'',
    emailForm:{newEmail:'', code:''},
    verifications:[], verifLoading:false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    initials(){
      var a=(this.form.first_name||'').charAt(0); var b=(this.form.last_name||'').charAt(0);
      return ((a+b) || (this.user.display_name||'?').charAt(0)).toUpperCase();
    },
    verifVerifiedCount(){ return this.verifications.filter(function(v){return v.verification_status==='verified'}).length; },
    verifPendingCount(){ return this.verifications.filter(function(v){return v.verification_status==='pending'}).length; },
    canSubmitVerification(){
      if (!this.verifications.length) return false;
      if (this.verifications.some(function(v){return v.verification_status==='verified'})) return false;
      return this.verifications[0] && this.verifications[0].verification_status === 'rejected';
    }
  },
  mounted(){
    if(!sessionStorage.getItem('userId')){this.$router.push('/login?redirect=/me');return}
    var self=this;
    this.themeObserver=new MutationObserver(function(){self.darkMode=document.documentElement.getAttribute('data-theme')!=='light'});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadProfile();
    this.loadCounties();
    this.loadVerifications();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect()},
  methods:{
    avatarUrl(p){
      if(!p)return''; if(/^https?:/i.test(p))return p;
      var base = uploads || flocker;
      return base + (p.charAt(0)==='/'?p.slice(1):p);
    },
    async loadProfile(){
      this.loading=true;
      try{
        var r=await executeJson({function:'getUserProfile',userId:sessionStorage.getItem('userId')||''},flocker);
        if(r.data&&r.data.success&&r.data.data&&r.data.data.user){
          this.user=r.data.data.user;
          this.form.first_name=this.user.first_name||'';
          this.form.last_name=this.user.last_name||'';
          this.form.email=this.user.email||'';
          this.form.phone=this.user.phone||'';
          this.form.avatar_url=this.user.avatar_url||'';
          // Keep navbars in sync — write back to session and broadcast for other components
          try {
            if (this.user.display_name) sessionStorage.setItem('userName', this.user.display_name);
            if (this.user.avatar_url) sessionStorage.setItem('userAvatar', this.user.avatar_url);
            window.dispatchEvent(new CustomEvent('flockr-profile-updated', { detail: { avatar_url: this.user.avatar_url, display_name: this.user.display_name } }));
          } catch(e){}
          this.form.county_id=this.user.county_id||'';
          this.form.sub_county_id=this.user.sub_county_id||'';
          this.form.address_details=this.user.address_details||'';
          this.form.preferred_language=this.user.preferred_language||'en';
          if(this.form.county_id) this.loadSubCounties(this.form.county_id);
        }
      }catch(e){}
      this.loading=false;
    },
    async loadCounties(){
      try{
        var r=await executeJson({function:'getCounties'},flocker);
        if(r.data&&r.data.success&&r.data.data) this.counties=r.data.data.counties||[];
      }catch(e){}
    },
    async loadSubCounties(countyId){
      this.subCounties=[];
      if(!countyId)return;
      try{
        var r=await executeJson({function:'getSubCounties',countyId:countyId},flocker);
        if(r.data&&r.data.success&&r.data.data) this.subCounties=r.data.data.subCounties||r.data.data.sub_counties||[];
      }catch(e){}
    },
    onCountyChange(){this.form.sub_county_id='';this.loadSubCounties(this.form.county_id)},
    onCropped(payload){
      // payload = { file: File, dataUrl: string } from AvatarCropper
      this.avatarFile = payload.file;
      this.avatarPreview = payload.dataUrl;
    },
    async saveProfile(){
      this.saving=true; this.profileSaved=false; this.profileError='';
      try{
        var fd=new FormData();
        fd.append('function','updateUserProfile');
        fd.append('userId',sessionStorage.getItem('userId')||'');
        fd.append('firstName',this.form.first_name||'');
        fd.append('lastName',this.form.last_name||'');
        if(this.form.county_id) fd.append('countyId',this.form.county_id);
        if(this.form.sub_county_id) fd.append('subCountyId',this.form.sub_county_id);
        if(this.form.address_details) fd.append('addressDetails',this.form.address_details);
        if(this.form.preferred_language) fd.append('preferredLanguage',this.form.preferred_language);
        if(this.avatarFile) fd.append('avatar',this.avatarFile);
        var r=await execute(fd,flocker);
        if(r.data&&r.data.success){
          this.profileSaved=true;
          this.avatarFile=null; this.avatarPreview='';
          this.loadProfile();
          setTimeout(()=>{this.profileSaved=false},3000);
        }else{
          this.profileError=(r.data&&r.data.message)||'Could not save profile';
        }
      }catch(e){this.profileError='Network error'}
      this.saving=false;
    },
    async requestPhoneChange(){
      this.phoneBusy=true; this.phoneError='';
      try{
        var r=await executeJson({function:'requestPhoneChange',userId:sessionStorage.getItem('userId')||'',newPhone:this.phoneForm.newPhone},flocker);
        if(r.data&&r.data.success){
          this.phoneStep='verify';
          if(r.data.data&&r.data.data.phone) this.phoneForm.newPhone=r.data.data.phone;
        }else{this.phoneError=(r.data&&r.data.message)||'Could not send code'}
      }catch(e){this.phoneError='Network error'}
      this.phoneBusy=false;
    },
    async confirmPhoneChange(){
      this.phoneBusy=true; this.phoneError='';
      try{
        var r=await executeJson({function:'confirmPhoneChange',userId:sessionStorage.getItem('userId')||'',newPhone:this.phoneForm.newPhone,code:this.phoneForm.code},flocker);
        if(r.data&&r.data.success){
          this.phoneStep='done';
          this.form.phone=r.data.data.phone||this.phoneForm.newPhone;
          this.phoneForm.code=''; this.phoneForm.newPhone='';
          this.loadProfile();
        }else{this.phoneError=(r.data&&r.data.message)||'Verification failed'}
      }catch(e){this.phoneError='Network error'}
      this.phoneBusy=false;
    },
    async requestEmailChange(){
      this.emailBusy=true; this.emailError='';
      try{
        var r=await executeJson({function:'requestEmailChange',userId:sessionStorage.getItem('userId')||'',newEmail:this.emailForm.newEmail},flocker);
        if(r.data&&r.data.success){
          this.emailStep='verify';
        }else{this.emailError=(r.data&&r.data.message)||'Could not send code'}
      }catch(e){this.emailError='Network error'}
      this.emailBusy=false;
    },
    async confirmEmailChange(){
      this.emailBusy=true; this.emailError='';
      try{
        var r=await executeJson({function:'confirmEmailChange',userId:sessionStorage.getItem('userId')||'',newEmail:this.emailForm.newEmail,code:this.emailForm.code},flocker);
        if(r.data&&r.data.success){
          this.emailStep='done';
          this.form.email=r.data.data.email||this.emailForm.newEmail;
          this.emailForm.code=''; this.emailForm.newEmail='';
          this.loadProfile();
        }else{this.emailError=(r.data&&r.data.message)||'Verification failed'}
      }catch(e){this.emailError='Network error'}
      this.emailBusy=false;
    },
    async loadVerifications(){
      this.verifLoading = true;
      try{
        var r = await executeJson({function:'getMyVerifications', userId: sessionStorage.getItem('userId')||''}, flocker);
        if(r.data && r.data.success && r.data.data) this.verifications = r.data.data.verifications || [];
      }catch(e){}
      this.verifLoading = false;
    },
    formatVerifType(t){ return (t||'verification').replace(/_/g,' ').replace(/\b\w/g, function(c){return c.toUpperCase()}); },
    formatDate(d){ if(!d)return''; try{return new Date(d).toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'numeric'})}catch(e){return d} },
    verifIcon(s){ return s==='verified'?'mdi-check-decagram': s==='rejected'?'mdi-close-circle': 'mdi-clock-outline'; },
    verifLabel(s){ return s==='verified'?'Verified': s==='rejected'?'Not approved': 'Under review'; }
  }
};
</script>

<style scoped>
.mp-root{min-height:100vh}
.mp-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.mp-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:12px;text-decoration:none}
.mp-container{padding-top:24px;padding-bottom:60px;max-width:1100px}
.mp-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:22px}
.mp-section-title{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:16px}
.mp-tabs{padding:8px;display:flex;flex-direction:column;gap:4px}
.mp-tab{background:transparent;border:none;color:var(--fm-m,#A09888);font-size:13px;font-weight:500;padding:10px 14px;border-radius:8px;text-align:left;cursor:pointer;}
.mp-tab:hover{background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8)}
.mp-tab-active{background:rgba(191,161,100,0.12);color:#BFA164;font-weight:600}
.mp-avatar{width:80px;height:80px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;overflow:hidden;font-size:30px;font-weight:700;color:#BFA164;flex-shrink:0}
.mp-avatar img{width:100%;height:100%;object-fit:cover}
.mp-label{display:block;color:var(--fm-s,#6B6356);font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px}
.mp-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E)!important;color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.mp-input:focus{border-color:#BFA164!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)!important}
.mp-otp{font-size:22px;letter-spacing:8px;text-align:center;font-weight:700}
.mp-hint{color:var(--fm-s,#6B6356);font-size:10px;margin-top:4px}
.mp-locked{color:var(--fm-s,#6B6356);background:var(--fm-bg2,#0F0F0F);border:1px dashed var(--fm-br,#1E1E1E);border-radius:8px;padding:10px 12px;font-size:11px}
.mp-current-pill{background:rgba(191,161,100,0.06);border:1px solid rgba(191,161,100,0.3);border-radius:10px;padding:10px 14px;color:var(--fm-h,#F5F0E8);font-size:13px}
.mp-verified{color:#50C878;font-size:11px;font-weight:600}
.mp-info{background:rgba(79,195,247,0.06);border:1px solid rgba(79,195,247,0.3);border-radius:8px;padding:10px 14px;color:#4FC3F7;font-size:12px}
.mp-success{background:rgba(80,200,120,0.08);border:1px solid #50C878;border-radius:8px;padding:12px 14px;color:#50C878;font-size:13px}
.mp-error{color:#FF6B6B;font-size:12px}
.mp-saved{color:#50C878;font-size:12px;align-self:center}

html[data-theme="light"] .mp-topbar{background:rgba(247,245,242,.96);border-bottom-color:#E8E2DA}
html[data-theme="light"] .mp-card{background:#FFFFFF;border-color:#E8E2DA;color:#1A1A1A}
html[data-theme="light"] .mp-input{background:#F7F5F2!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .mp-locked{background:#F7F5F2;border-color:#E0D8D0;color:#777}
html[data-theme="light"] .mp-tab:hover{background:#F0EBE4;color:#1A1A1A}
html[data-theme="light"] .mp-current-pill{color:#1A1A1A}

.mp-verif-pill{font-size:9px;padding:2px 6px;border-radius:8px;margin-left:6px;font-weight:700}
.mp-verif-pill-ok{background:#50C878;color:#0A0A0A}
.mp-verif-pill-pending{background:#FF9800;color:#0A0A0A}
.mp-verif-card{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px 14px;margin-bottom:10px}
html[data-theme="light"] .mp-verif-card{background:#F7F5F2;border-color:#E0D8D0}
.mp-verif-card-verified{border-color:rgba(80,200,120,.5)}
.mp-verif-card-rejected{border-color:rgba(255,107,107,.5)}
.mp-verif-card-pending{border-color:rgba(255,152,0,.5)}
.mp-verif-type{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;text-transform:capitalize}
.mp-verif-date{color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px}
.mp-verif-status{display:inline-flex;align-items:center;gap:4px;font-size:12px;font-weight:700;padding:4px 10px;border-radius:8px}
.mp-verif-status i{font-size:14px}
.mp-verif-status-verified{background:rgba(80,200,120,.12);color:#50C878}
.mp-verif-status-rejected{background:rgba(255,107,107,.12);color:#FF6B6B}
.mp-verif-status-pending{background:rgba(255,152,0,.12);color:#FF9800}
.mp-verif-reason{margin-top:8px;padding:8px 12px;background:rgba(255,107,107,.08);border:1px solid rgba(255,107,107,.3);border-radius:8px;color:#FF8A8A;font-size:12px}
html[data-theme="light"] .mp-verif-reason{color:#C44747}
.mp-verif-empty{text-align:center;padding:30px 16px}
</style>
